import { useEffect, useState } from "react";
import { track } from "../../analytics";
import type { ClipTag, MediaItem } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { userApi } from "../../lib/api";
import { reportClipView } from "../../lib/clipViews";
import { useSession } from "../../lib/session";
import { TextInput, cx } from "../ui/primitives";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { AddToCollection } from "./AddToCollection";
import { AuthModal } from "./AuthModal";
import { ClipStats } from "./ClipStats";
import { ClipViewer } from "./ClipViewer";
import { TagBubble, TagBubbleRow } from "./TagBubble";
import { CrownMark } from "../ui/Crown";
import { useFapsStore } from "../../lib/faps";

/**
 * The clip detail panel, opened by clicking a clip card (ClipCard). Everyone gets a preview:
 * the clip plays, plus fap (crown), add-to-collection, its approved tags + a suggest-
 * a-tag box, and stats. Built on the shared ClipViewer shell (same shell the admin
 * clip editor uses); admin-only affordances live in ClipLightbox, not here.
 */
export function ClipEditModal({
  clip,
  list,
  onClose,
}: {
  clip: MediaItem;
  /** When provided, ‹ › buttons + arrow keys navigate within this list (e.g. a
   *  collection's clips or the catalog page). Omit for a standalone single preview. */
  list?: MediaItem[];
  onClose: () => void;
}) {
  const admin = useIsAdmin();
  const user = useSession((s) => s.user);

  // Navigate within an optional list. `index` tracks the position; it re-seeks whenever
  // the parent opens a different clip. `current` is the clip actually shown.
  const seek = (c: MediaItem) =>
    list
      ? Math.max(
          0,
          list.findIndex((x) => clipKey(x) === clipKey(c)),
        )
      : 0;
  const [index, setIndex] = useState(() => seek(clip));
  useEffect(() => {
    setIndex(seek(clip));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipKey(clip)]);
  const current = list && list.length > 0 ? (list[index] ?? clip) : clip;
  const key = clipKey(current);
  const onPrev =
    list && index > 0 ? () => setIndex((i) => Math.max(0, i - 1)) : undefined;
  const onNext =
    list && index < list.length - 1
      ? () => setIndex((i) => Math.min(list.length - 1, i + 1))
      : undefined;

  // Locally-entered tag suggestions this session (users can only SUGGEST; an admin
  // reviews before they apply). Removing a chip just clears it locally.
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  // The clip's current approved (global) tags — shown read-only. The GET is public.
  // Each carries its display name + provenance so AI-assigned ones can be badged.
  const [approved, setApproved] = useState<ClipTag[] | null>(null);
  // Bumped after a report is filed, to re-pull this clip's keywords.
  const [tagsNonce, setTagsNonce] = useState(0);

  // Reset the pending suggestions when the shown clip changes (navigation included).
  useEffect(() => {
    setTags([]);
    setTagInput("");
  }, [key]);

  // Load the shown clip's existing tags to display.
  useEffect(() => {
    let cancelled = false;
    setApproved(null);
    userApi
      .getClipTags(current)
      .then((r) => !cancelled && setApproved(r.tags))
      .catch(() => !cancelled && setApproved([]));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tagsNonce]);

  /** Submit a new tag as a pending suggestion (admin-reviewed before it's applied). */
  function suggestTag(value?: string) {
    const t = (value ?? tagInput).trim().slice(0, 40);
    setTagInput("");
    if (!t || tags.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    setTags((prev) => [...prev, t]);
    void userApi.suggestTag(current, t).catch(() => undefined);
  }

  return (
    <ClipViewer
      clip={current}
      title="Details"
      onClose={onClose}
      onPrev={onPrev}
      onNext={onNext}
      /* Somebody clicked a clip and is watching it: that is a view. Once per clip per
         page — lib/clipViews.ts dedups, so canplaythrough re-firing after a stall or
         re-opening the same clip in this tab does not count twice. */
      onPlayable={() => reportClipView(current)}
    >
      {/* fap + add to collection — both show for everyone; anon clicks prompt sign-up */}
      <div className="flex items-center gap-2">
        <FapToggle clip={current} />
        <AddToCollection clip={current} />
      </div>

      {/* current (approved) tags — read-only */}
      <div>
        <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-zinc-400">
          Tags
        </div>
        {approved === null ? (
          <span className="text-xs text-zinc-600">Loading…</span>
        ) : approved.length === 0 ? (
          <span className="text-xs text-zinc-600">No tags yet.</span>
        ) : (
          <TagBubbleRow>
            {approved.map((t) => (
              <TagBubble
                key={t.tag}
                tag={t}
                clip={current}
                onNavigate={onClose}
                onReported={() => setTagsNonce((n) => n + 1)}
              />
            ))}
          </TagBubbleRow>
        )}
      </div>

      {/* suggest a tag (pending admin review) — signed-in only */}
      {user ? (
        <div>
          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-zinc-400">
            Suggest tags
          </div>
          {tags.length > 0 && (
            <TagBubbleRow className="mb-2">
              {tags.map((t) => (
                <TagBubble
                  key={t}
                  tag={t}
                  onRemove={() =>
                    setTags((prev) => prev.filter((x) => x !== t))
                  }
                />
              ))}
            </TagBubbleRow>
          )}
          <TextInput
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                suggestTag();
              }
            }}
            placeholder="Suggest a tag + Enter…"
            maxLength={40}
          />
          <p className="mt-1 text-[10px] text-zinc-500">
            Suggested tags are reviewed by an admin.
          </p>
        </div>
      ) : null}

      {/* stats */}
      <div>
        <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-zinc-400">
          Stats
        </div>
        <ClipStats clip={current} showTechnical={admin} />
      </div>
    </ClipViewer>
  );
}

/** A crown that faps/un-faps the clip for the signed-in user (anon → sign-in). */
function FapToggle({ clip }: { clip: MediaItem }) {
  const user = useSession((s) => s.user);
  const [fapped, setFapped] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const key = clipKey(clip);

  useEffect(() => {
    let cancelled = false;
    userApi
      .fapStatus(clip)
      .then((s) => !cancelled && setFapped(s.fapped))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  async function toggle() {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    const prev = fapped;
    setFapped(!prev);
    try {
      const s = await userApi.toggleFap(clip);
      // A toggle can un-fap; only the save counts.
      if (s.fapped) track("fap_saved", { surface: "clip" });
      setFapped(s.fapped);
      // The whole reply — the store treats the OFF leg's absent `fapsEarned` as nothing.
      useFapsStore.getState().applyClick(s);
    } catch {
      setFapped(prev);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-pressed={fapped}
        className={cx(
          "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
          fapped
            ? "border-[#f5c518]/50 bg-[#f5c518]/15 text-[#f5c518]"
            : "border-[var(--color-edge)] bg-[var(--color-panel-2)] text-zinc-200 hover:border-[#f5c518]/40",
        )}
      >
        <CrownMark size={12} />
        {fapped ? "Fapped" : "Fap"}
      </button>
      {authOpen && (
        <AuthModal
          initialMode="register"
          reason="Create a free account to fap this clip."
          onClose={() => setAuthOpen(false)}
        />
      )}
    </>
  );
}
